import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Modal,
  Tooltip,
  Grid,
  Chip,
  IconButton,
  TextField,
  Alert,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HistoryIcon from "@mui/icons-material/History";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  getBudgetRecords,
  getResourceConfigs,
  clearBudgetState,
  clearBudgetMessages,
  getOrgBudgets,
  createOrgBudget,
  updateOrgBudget,
  deleteOrgBudget,
  deleteBudgetRecord,
  getBudgetHistory,
  getOrgBudgetHistory,
} from "../../store/spendCategorization/budgetSlice";
import { Snackbar, Alert as MuiAlert } from "@mui/material";
import OrgBudgetAlertsModal from "./components/OrgBudgetAlertsModal";
import BudgetGroupAlertsModal from "./components/BudgetGroupAlertsModal";
import BudgetRecordModal from "./components/BudgetRecordModal";
import DeleteConfirmationDialog from "./components/DeleteConfirmationDialog";

// Available Accounts Modal Component - UNIQUE_IDENTIFIER_AVAILABLE
const AvailableAccountsModal = ({
  open,
  onClose,
  allAccounts,
  accountsInDefaultResourceGroup,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Filter accounts based on search term
  const filteredAccounts = useMemo(() => {
    if (!searchTerm.trim()) return allAccounts;
    
    return allAccounts.filter(account => 
      account?.accnumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account?.accname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account?.resource_group_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allAccounts, searchTerm]);

  if (!allAccounts || allAccounts.length === 0) return null;

  // Group filtered accounts by resource group name
  const groupedAccounts = filteredAccounts.reduce((groups, account) => {
    const groupName = account?.resource_group_name || "Default Budget Group";
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(account);
    return groups;
  }, {});

  // Sort groups alphabetically
  const sortedGroupNames = Object.keys(groupedAccounts).sort();

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="available-accounts-modal-title"
      aria-describedby="available-accounts-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 1000,
          maxHeight: "80vh",
          overflow: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#fafbfc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "1.25rem",
            }}
          >
            Unassigned accounts
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              mt: 1,
              fontWeight: 400,
            }}
          >
            Identify and assign a proper budget group for these {allAccounts.length} account{allAccounts.length !== 1 ? "s" : ""}. Leaving these accounts unassigned may result in budget overage.
          </Typography>
          

          
          {/* Search Input */}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search accounts by number, name, or budget group..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ color: "#9ca3af" }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderColor: "#d1d5db",
                  "&:hover": {
                    borderColor: "#9ca3af",
                  },
                  "&.Mui-focused": {
                    borderColor: "#3b82f6",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  fontSize: "0.875rem",
                  fontFamily: "Inter, sans-serif",
                },
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Simple Expandable Table */}
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: "#f8fafc",
                  "& th": {
                    borderBottom: "2px solid #e2e8f0",
                    padding: "16px",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "#374151",
                    textTransform: "none"
                  }
                }}>
                  <TableCell>Budget Group</TableCell>
                  <TableCell align="center">Number of Accounts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedGroupNames.map((groupName) => (
                  <React.Fragment key={groupName}>
                    {/* Main Group Row */}
                    <TableRow 
                      sx={{ 
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#f8fafc" },
                        "& td": {
                          padding: "16px",
                          borderBottom: "1px solid #e2e8f0",
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.875rem"
                        }
                      }}
                      onClick={() => {
                        setExpandedGroups(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(groupName)) {
                            newSet.delete(groupName);
                          } else {
                            newSet.add(groupName);
                          }
                          return newSet;
                        });
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <KeyboardArrowDownIcon 
                            sx={{ 
                              color: "#64748b", 
                              fontSize: 18,
                              transition: "transform 0.2s ease-in-out",
                            }} 
                          />
                          {groupName}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#64748b" }}>
                        {groupedAccounts[groupName].length} account{groupedAccounts[groupName].length !== 1 ? 's' : ''}
                      </TableCell>
                    </TableRow>
                    
                    {/* Expandable Accounts Row */}
                    <TableRow sx={{ display: expandedGroups.has(groupName) ? "table-row" : "none" }}>
                      <TableCell colSpan={2} sx={{ padding: 0, borderBottom: "1px solid #e2e8f0" }}>
                        <Box sx={{ 
                          backgroundColor: "#f8fafc", 
                          borderLeft: "4px solid #3b82f6",
                          margin: "8px 16px",
                          borderRadius: "4px"
                        }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ 
                                backgroundColor: "#f1f5f9",
                                "& th": {
                                  padding: "12px 16px",
                                  fontFamily: "Inter, sans-serif",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  color: "#475569",
                                  textTransform: "none",
                                  letterSpacing: "0.05em"
                                }
                              }}>
                                <TableCell>Account Number</TableCell>
                                <TableCell>Account Name</TableCell>
                                <TableCell>Spend</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {groupedAccounts[groupName].map((account, index) => (
                                <TableRow
                                  key={account?.accnumber || index}
                                  sx={{
                                    "&:hover": { backgroundColor: "#f8fafc" },
                                    "&:nth-of-type(even)": { backgroundColor: "#fafbfc" },
                                    "& td": {
                                      padding: "12px 16px",
                                      borderBottom: "1px solid #e2e8f0",
                                      fontFamily: "Inter, sans-serif",
                                      fontSize: "0.875rem"
                                    }
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                                    {account?.accnumber + (account.status === "ONBOARDED" ? " - Onboarded" : "") || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ color: "#374151" }}>
                                    {account?.accname || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ color: "#374151", fontWeight: 500 }}>
                                    {account?.org_budget_usage !== undefined && account?.org_budget_usage !== null
                                      ? new Intl.NumberFormat("en-US", {
                                          style: "currency",
                                          currency: "USD",
                                        }).format(account.org_budget_usage)
                                      : "$0.00"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Assigned Accounts Modal Component
const AssignedAccountsModal = ({
  open,
  onClose,
  assignedAccounts,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Filter accounts based on search term
  const filteredAccounts = useMemo(() => {
    if (!searchTerm.trim()) return assignedAccounts;
    
    return assignedAccounts.filter(account => 
      account?.accnumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account?.accname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account?.resource_group_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [assignedAccounts, searchTerm]);

  if (!assignedAccounts || assignedAccounts.length === 0) return null;

  // Group filtered accounts by resource group name
  const groupedAccounts = filteredAccounts.reduce((groups, account) => {
    const groupName = account?.resource_group_name || "Default Budget Group";
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(account);
    return groups;
  }, {});

  // Sort groups alphabetically
  const sortedGroupNames = Object.keys(groupedAccounts).sort();

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="assigned-accounts-modal-title"
      aria-describedby="assigned-accounts-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 1000,
          maxHeight: "80vh",
          overflow: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#fafbfc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "1.25rem",
            }}
          >
            Assigned Accounts
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              mt: 0.5,
            }}
          >
            Total assigned account{assignedAccounts.length !== 1 ? "s" : ""} : {assignedAccounts.length}
          </Typography>
          
          {/* Search Input */}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search assigned accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ color: "#9ca3af" }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderColor: "#d1d5db",
                  "&:hover": {
                    borderColor: "#9ca3af",
                  },
                  "&.Mui-focused": {
                    borderColor: "#3b82f6",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  fontSize: "0.875rem",
                  fontFamily: "Inter, sans-serif",
                },
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Simple Expandable Table */}
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: "#f8fafc",
                  "& th": {
                    borderBottom: "2px solid #e2e8f0",
                    padding: "16px",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "#374151",
                    textTransform: "none"
                  }
                }}>
                  <TableCell>Budget Group</TableCell>
                  <TableCell align="center">Number of Accounts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedGroupNames.map((groupName) => (
                  <React.Fragment key={groupName}>
                    {/* Main Group Row */}
                    <TableRow 
                      sx={{ 
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#f8fafc" },
                        "& td": {
                          padding: "16px",
                          borderBottom: "1px solid #e2e8f0",
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.875rem"
                        }
                      }}
                      onClick={() => {
                        setExpandedGroups(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(groupName)) {
                            newSet.delete(groupName);
                          } else {
                            newSet.add(groupName);
                          }
                          return newSet;
                        });
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <KeyboardArrowDownIcon 
                            sx={{ 
                              color: "#64748b", 
                              fontSize: 18,
                              transition: "transform 0.2s ease-in-out",
                            }} 
                          />
                          {groupName}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#64748b" }}>
                        {groupedAccounts[groupName].length} account{groupedAccounts[groupName].length !== 1 ? 's' : ''}
                      </TableCell>
                    </TableRow>
                    
                    {/* Expandable Accounts Row */}
                    <TableRow sx={{ display: expandedGroups.has(groupName) ? "table-row" : "none" }}>
                      <TableCell colSpan={2} sx={{ padding: 0, borderBottom: "1px solid #e2e8f0" }}>
                        <Box sx={{ 
                          backgroundColor: "#f8fafc", 
                          borderLeft: "4px solid #3b82f6",
                          margin: "8px 16px",
                          borderRadius: "4px"
                        }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ 
                                backgroundColor: "#f1f5f9",
                                "& th": {
                                  padding: "12px 16px",
                                  fontFamily: "Inter, sans-serif",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  color: "#475569",
                                  textTransform: "none",
                                  letterSpacing: "0.05em"
                                }
                              }}>
                                <TableCell>Account Number</TableCell>
                                <TableCell>Account Name</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {groupedAccounts[groupName].map((account, index) => (
                                <TableRow
                                  key={account?.accnumber || index}
                                  sx={{
                                    "&:hover": { backgroundColor: "#f8fafc" },
                                    "&:nth-of-type(even)": { backgroundColor: "#fafbfc" },
                                    "& td": {
                                      padding: "12px 16px",
                                      borderBottom: "1px solid #e2e8f0",
                                      fontFamily: "Inter, sans-serif",
                                      fontSize: "0.875rem"
                                    }
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                                    {account?.accnumber + (account.status === "ONBOARDED" ? " - Onboarded" : "") || "N/A"}
                                  </TableCell>
                                  <TableCell sx={{ color: "#374151" }}>
                                    {account?.accname || "N/A"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Default Resource Group Accounts Modal Component
const DefaultResourceGroupAccountsModal = ({
  open,
  onClose,
  accountsInDefaultResourceGroup,
  allAccounts,
}) => {
  if (
    !accountsInDefaultResourceGroup ||
    accountsInDefaultResourceGroup.length === 0
  )
    return null;

  // Map account numbers to full account objects
  const accountDetails = accountsInDefaultResourceGroup.map((accountNumber) => {
    const fullAccount = allAccounts?.find(
      (acc) => acc.accnumber === accountNumber
    );
    return (
      fullAccount || {
        accnumber: accountNumber,
        accname: "Unknown Account",
        status: "No Status",
      }
    );
  });

  const accountColumns = [
    {
      field: "accnumber",
      headerName: "Account Number",
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: "0.8rem",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "accname",
      headerName: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Account Name
          </Typography>
        </Box>
      ),
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#374151",
            fontSize: "0.8rem",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "org_budget_usage",
      headerName: "Spend",
      flex: 1.5,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#374151",
            fontSize: "0.8rem",
            fontWeight: 500,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {params.value !== undefined && params.value !== null
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(params.value)
            : "$0.00"}
        </Typography>
      ),
    },
  ];

  const accountRows = accountDetails.map((account, index) => ({
    id: account.accnumber || index,
    ...account,
    status: account.status || "No Status",
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="default-resource-group-accounts-modal-title"
      aria-describedby="default-resource-group-accounts-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          maxHeight: "80vh",
          overflow: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "1.25rem",
            }}
          >
            Accounts in Default Budget Group
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              mt: 0.5,
            }}
          >
            {accountDetails.length} account
            {accountDetails.length !== 1 ? "s" : ""} in Default Budget Group
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              height: 400,
              "& .MuiDataGrid-root": {
                border: "none",
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F5F5F5",
                borderBottom: "1px solid #e2e8f0",
                "& .MuiDataGrid-columnHeader": {
                  borderRight: "1px solid #e2e8f0",
                  "&:last-child": {
                    borderRight: "none",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                  color: "#333",
                  fontSize: "0.8rem",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f3f4f6",
                fontSize: "0.8rem",
                color: "#374151",
                "&:focus": {
                  outline: "none",
                },
                padding: "6px 12px",
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "#f8fafc",
                },
              },
              "& .MuiDataGrid-row.status-no-status, .MuiDataGrid-row.status-shadow":
                {
                  backgroundColor: "#f3f4f6",
                  "&:hover": {
                    backgroundColor: "#e5e7eb",
                  },
                },
              "& .MuiDataGrid-row.status-onboarded": {
                backgroundColor: "#ffffff",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: "white",
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
              },
            }}
          >
            <DataGrid
              rows={accountRows}
              columns={accountColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              disableColumnMenu
              getRowClassName={(params) =>
                `status-${params.row.status
                  ?.replace(/\s+/g, "-")
                  .toLowerCase()}`
              }
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
                "& .MuiDataGrid-cell": {
                  py: 0.5,
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F5F5F5",
                },
              }}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Accounts Modal Component
const AccountsModal = ({ open, onClose, record }) => {
  if (!record || !record.accounts) return null;

  const accountColumns = [
    {
      field: "accnumber",
      headerName: "Account Number",
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: "0.8rem",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {params.value + (params.row.status === "ONBOARDED" ? " - Onboarded" : "")}
        </Typography>
      ),
    },
    {
      field: "accname",
      headerName: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Account Name
          </Typography>
        </Box>
      ),
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#374151",
            fontSize: "0.8rem",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    // {
    //   field: "org_budget_usage",
    //   headerName: "Spend",
    //   flex: 1.5,
    //   minWidth: 120,
    //   renderCell: (params) => (
    //     <Typography
    //       variant="body2"
    //       sx={{
    //         color: "#374151",
    //         fontSize: "0.8rem",
    //         fontWeight: 500,
    //         fontFamily:
    //           '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    //       }}
    //     >
    //       {params.value !== undefined && params.value !== null
    //         ? new Intl.NumberFormat("en-US", {
    //             style: "currency",
    //             currency: "USD",
    //           }).format(params.value)
    //         : "$0.00"}
    //     </Typography>
    //   ),
    // },
  ];

  const accountRows = record.accounts.map((account, index) => ({
    id: account.accnumber || index,
    ...account,
    status: account.status || "No Status",
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="accounts-modal-title"
      aria-describedby="accounts-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          maxHeight: "80vh",
          overflow: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "1.25rem",
            }}
          >
            Account Details - {record.resource_group_name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              mt: 0.5,
            }}
          >
            Budget: {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: record.currency || "USD",
            }).format(record.amount)}
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              height: 400,
              "& .MuiDataGrid-root": {
                border: "none",
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F5F5F5",
                borderBottom: "1px solid #e2e8f0",
                "& .MuiDataGrid-columnHeader": {
                  borderRight: "1px solid #e2e8f0",
                  "&:last-child": {
                    borderRight: "none",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                  color: "#333",
                  fontSize: "0.8rem",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f3f4f6",
                fontSize: "0.8rem",
                color: "#374151",
                "&:focus": {
                  outline: "none",
                },
                padding: "6px 12px",
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "#f8fafc",
                },
              },
              "& .MuiDataGrid-row.status-no-status, .MuiDataGrid-row.status-shadow":
                {
                  backgroundColor: "#f3f4f6",
                  "&:hover": {
                    backgroundColor: "#e5e7eb",
                  },
                },
              "& .MuiDataGrid-row.status-onboarded": {
                backgroundColor: "#ffffff",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: "white",
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
              },
            }}
          >
            <DataGrid
              rows={accountRows}
              columns={accountColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              disableColumnMenu
              getRowClassName={(params) =>
                `status-${params.row.status
                  ?.replace(/\s+/g, "-")
                  .toLowerCase()}`
              }
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
                "& .MuiDataGrid-cell": {
                  py: 0.5,
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F5F5F5",
                },
              }}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Organization Budget Modal Component
const OrgBudgetModal = ({ open, onClose, budget, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    amount: "",
    start_date: "",
    end_date: "",
    description: "",
    admin_name: "",
    admin_email: "",
    admin_phone: "",
  });
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  useEffect(() => {
    if (budget) {
      const newFormData = {
        amount: budget.amount?.toString() || "",
        start_date: budget.start_date || "",
        end_date: budget.end_date || "",
        description: budget.description || "",
        admin_name: budget.admin_name || "",
        admin_email: budget.admin_email || "",
        admin_phone: budget.admin_phone || "",
      };
      setFormData(newFormData);
      setOriginalData(newFormData);
    } else {
      const emptyFormData = {
        amount: "",
        start_date: "",
        end_date: "",
        description: "",
        admin_name: "",
        admin_email: "",
        admin_phone: "",
      };
      setFormData(emptyFormData);
      setOriginalData(null);
    }
    setErrors({});
  }, [budget, open]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    if (!originalData) return false; // No changes for new budget creation
    
    return (
      formData.amount !== originalData.amount ||
      formData.start_date !== originalData.start_date ||
      formData.end_date !== originalData.end_date ||
      formData.description !== originalData.description ||
      formData.admin_name !== originalData.admin_name ||
      formData.admin_email !== originalData.admin_email ||
      formData.admin_phone !== originalData.admin_phone
    );
  }, [formData, originalData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (!formData.end_date) {
      newErrors.end_date = "End date is required";
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (startDate >= endDate) {
        newErrors.end_date = "End date must be after start date";
      }
    }

    if (!formData.admin_name || formData.admin_name.trim() === "") {
      newErrors.admin_name = "Admin name is required";
    }

    if (!formData.admin_email || formData.admin_email.trim() === "") {
      newErrors.admin_email = "Admin email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.admin_email)) {
      newErrors.admin_email = "Please enter a valid email address";
    }

    if (!formData.admin_phone || formData.admin_phone.trim() === "") {
      newErrors.admin_phone = "Admin phone is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        ...formData,
        amount: parseFloat(formData.amount),
        currency: "USD", // Always use USD
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="org-budget-modal-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 600,
          maxHeight: "90vh",
          overflow: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#1e293b" }}>
            {budget ? "Edit Organization Budget & Contact" : "Create Organization Budget & Contact"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "#64748b" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Amount with fixed USD currency */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Budget Amount *"
                type="number"
                value={formData.amount}
                onChange={(e) => handleInputChange("amount", e.target.value)}
                error={!!errors.amount}
                helperText={errors.amount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          backgroundColor: "#f3f4f6",
                          borderRadius: "6px",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          fontWeight: 500,
                          border: "1px solid #d1d5db",
                        }}
                      >
                        USD
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date *"
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  handleInputChange("start_date", e.target.value)
                }
                error={!!errors.start_date}
                helperText={errors.start_date}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date *"
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange("end_date", e.target.value)}
                error={!!errors.end_date}
                helperText={errors.end_date}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Enter budget description (optional)"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </Grid>

            {/* Admin Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Admin Name *"
                value={formData.admin_name}
                onChange={(e) =>
                  handleInputChange("admin_name", e.target.value)
                }
                placeholder="Enter admin name"
                error={!!errors.admin_name}
                helperText={errors.admin_name}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </Grid>

            {/* Admin Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Admin Email *"
                type="email"
                value={formData.admin_email}
                onChange={(e) =>
                  handleInputChange("admin_email", e.target.value)
                }
                placeholder="Enter admin email"
                error={!!errors.admin_email}
                helperText={errors.admin_email}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </Grid>

            {/* Admin Phone */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin Phone *"
                value={formData.admin_phone}
                onChange={(e) =>
                  handleInputChange("admin_phone", e.target.value)
                }
                placeholder="Enter admin phone"
                error={!!errors.admin_phone}
                helperText={errors.admin_phone}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Actions */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
              sx={{
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || (budget && !hasChanges)}
              sx={{
                backgroundColor: "#3b82f6",
                color: "white",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#2563eb",
                },
                "&:disabled": {
                  backgroundColor: "#9ca3af",
                },
              }}
            >
              {isSubmitting
                ? "Saving..."
                : budget
                ? "Update Budget"
                : "Create Budget"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

// Organization Budget History Modal Component
const OrgBudgetHistoryModal = ({
  open,
  onClose,
  orgBudgetHistory,
  isFetchingOrgBudgetHistory,
  fetchOrgBudgetHistoryError,
  expandedRows,
  onRowToggle,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="org-budget-history-modal-title"
      aria-describedby="org-budget-history-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "90%", md: "80%" },
          maxWidth: "1200px",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: 24,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 4px rgba(59, 130, 246, 0.3)",
              }}
            >
              <HistoryIcon sx={{ fontSize: 20, color: "white" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "1.125rem",
                }}
              >
                Organization Budget History
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Track changes to organization budgets
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#374151",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, maxHeight: "calc(90vh - 120px)", overflow: "auto" }}>
          {/* Organization Budget History Table */}
          <Box>
              
              {isFetchingOrgBudgetHistory ? (
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      Loading organization budget history...
                    </Typography>
                  </Box>
                </Box>
              ) : fetchOrgBudgetHistoryError ? (
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#dc2626", textAlign: "center" }}
                  >
                    Error loading organization budget history: {fetchOrgBudgetHistoryError}
                  </Typography>
                </Box>
              ) : orgBudgetHistory && orgBudgetHistory.length > 0 ? (
                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell sx={{ width: 50 }}></TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Changed By</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Changes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orgBudgetHistory.map((item, index) => {
                        const isExpanded = expandedRows.has(index);
                        const delta = item.delta;
                        const hasChanges = delta && Object.keys(delta).length > 0;
                        
                        return (
                          <React.Fragment key={index}>
                            <TableRow 
                              sx={{ 
                                '&:hover': { backgroundColor: '#f8fafc' },
                                cursor: hasChanges ? 'pointer' : 'default'
                              }}
                              onClick={() => hasChanges && onRowToggle(index)}
                            >
                              <TableCell>
                                {hasChanges && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onRowToggle(index);
                                    }}
                                  >
                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                                  {new Date(item.changed_at + "Z").toLocaleString(undefined, {
                                    year: 'numeric',
                                    month: 'numeric',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    timeZoneName: 'short'
                                  })}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.change_type}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      item.change_type === "CREATE"
                                        ? "#059669"
                                        : item.change_type === "UPDATE"
                                        ? "#3b82f6"
                                        : "#dc2626",
                                    color: "white",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                                  {item.changed_by}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {hasChanges ? (
                                  <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                    {Object.keys(delta).length} field(s) changed
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                    No changes
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                            
                            {hasChanges && (
                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ margin: 1, p: 2, backgroundColor: "#f8fafc", borderRadius: "6px" }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "#374151" }}>
                                        Changes Made:
                                      </Typography>
                                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        {Object.entries(delta).map(([key, value], deltaIndex) => {
                                          // Handle special cases for accounts_added and accounts_removed
                                          if (key === "accounts_added" || key === "accounts_removed") {
                                            const accounts = Array.isArray(value) ? value : [];
                                            if (accounts.length === 0) return null; // Don't show empty arrays
                                            
                                            return (
                                              <Box key={deltaIndex} sx={{ 
                                                display: "flex", 
                                                flexDirection: "column",
                                                p: 1.5,
                                                backgroundColor: "white",
                                                borderRadius: "4px",
                                                border: "1px solid #e5e7eb"
                                              }}>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 600,
                                                    color: "#374151",
                                                    fontSize: "0.875rem",
                                                    mb: 1,
                                                    textTransform: "capitalize"
                                                  }}
                                                >
                                                  {key === "accounts_added" ? "Accounts Added" : "Accounts Removed"}
                                                </Typography>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                  {accounts.map((account, accIndex) => (
                                                    <Typography
                                                      key={accIndex}
                                                      variant="body2"
                                                      sx={{
                                                        color: key === "accounts_added" ? "#059669" : "#dc2626",
                                                        fontSize: "0.875rem",
                                                        fontFamily: "monospace",
                                                        backgroundColor: key === "accounts_added" ? "#f0fdf4" : "#fef2f2",
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: "4px",
                                                        border: `1px solid ${key === "accounts_added" ? "#bbf7d0" : "#fecaca"}`,
                                                        textAlign: "center",
                                                        fontWeight: 600
                                                      }}
                                                    >
                                                      {account}
                                                    </Typography>
                                                  ))}
                                                </Box>
                                              </Box>
                                            );
                                          }
                                          
                                          // Handle regular delta fields
                                          return (
                                            <Box key={deltaIndex} sx={{ 
                                              display: "flex", 
                                              flexDirection: "column",
                                              p: 1.5,
                                              backgroundColor: "white",
                                              borderRadius: "4px",
                                              border: "1px solid #e5e7eb"
                                            }}>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: "#374151",
                                                  fontSize: "0.875rem",
                                                  mb: 1,
                                                  textTransform: "capitalize"
                                                }}
                                              >
                                                {key.replace(/_/g, " ")}
                                              </Typography>
                                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                  <Typography variant="caption" sx={{ color: "#64748b", mb: 0.5 }}>
                                                    Previous Value
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      color: "#dc2626",
                                                      fontSize: "0.875rem",
                                                      fontFamily: "monospace",
                                                      backgroundColor: "#fef2f2",
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: "4px",
                                                      border: "1px solid #fecaca",
                                                      minWidth: "80px",
                                                      textAlign: "center"
                                                    }}
                                                  >
                                                    {key === "amount" ? (value.from ? `$${parseFloat(value.from).toLocaleString()}` : "N/A") : (value.from || "N/A")}
                                                  </Typography>
                                                </Box>
                                                
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      color: "#64748b",
                                                      fontSize: "1rem",
                                                      fontWeight: 600
                                                    }}
                                                  >
                                                    →
                                                  </Typography>
                                                </Box>
                                                
                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                  <Typography variant="caption" sx={{ color: "#64748b", mb: 0.5 }}>
                                                    New Value
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      color: "#059669",
                                                      fontSize: "0.875rem",
                                                      fontFamily: "monospace",
                                                      backgroundColor: "#f0fdf4",
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: "4px",
                                                      border: "1px solid #bbf7d0",
                                                      minWidth: "80px",
                                                      textAlign: "center"
                                                    }}
                                                  >
                                                    {key === "amount" ? (value.to ? `$${parseFloat(value.to).toLocaleString()}` : "N/A") : (value.to || "N/A")}
                                                    {key === "amount" && (() => {
                                                      const percentageChange = calculatePercentageChange(value.from, value.to);
                                                      if (percentageChange !== null) {
                                                        const sign = percentageChange >= 0 ? "+" : "";
                                                        const color = percentageChange >= 0 ? "#059669" : "#dc2626";
                                                        return (
                                                          <Box
                                                            component="span"
                                                            sx={{
                                                              display: "block",
                                                              fontSize: "0.75rem",
                                                              color: color,
                                                              fontWeight: 600,
                                                              mt: 0.5
                                                            }}
                                                          >
                                                            ({sign}{percentageChange.toFixed(1)}%)
                                                          </Box>
                                                        );
                                                      }
                                                      return null;
                                                    })()}
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748b", textAlign: "center" }}
                  >
                    No organization budget history data available
                  </Typography>
                </Box>
              )}
            </Box>
        </Box>
      </Box>
    </Modal>
  );
};

// Budget History Modal Component
const BudgetHistoryModal = ({
  open,
  onClose,
  budgetHistory,
  isFetchingBudgetHistory,
  fetchBudgetHistoryError,
  expandedRows,
  onRowToggle,
  selectedBudget,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="budget-history-modal-title"
      aria-describedby="budget-history-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "90%", md: "80%" },
          maxWidth: "1200px",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: 24,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 4px rgba(59, 130, 246, 0.3)",
              }}
            >
              <HistoryIcon sx={{ fontSize: 20, color: "white" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "1.125rem",
                }}
              >
                Budget History
              </Typography>
              {selectedBudget && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {selectedBudget.resource_group_name} - {selectedBudget.start_date} to {selectedBudget.end_date}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#374151",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, maxHeight: "calc(90vh - 120px)", overflow: "auto" }}>
          {isFetchingBudgetHistory ? (
            <Box
              sx={{
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Loading budget history...
                </Typography>
              </Box>
            </Box>
          ) : fetchBudgetHistoryError ? (
            <Box
              sx={{
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#dc2626", textAlign: "center" }}
              >
                Error loading budget history: {fetchBudgetHistoryError}
              </Typography>
            </Box>
          ) : budgetHistory && budgetHistory.length > 0 ? (
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ width: 50 }}></TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Changed By</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>Changes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {budgetHistory.map((item, index) => {
                    const isExpanded = expandedRows.has(index);
                    const delta = item.delta;
                    const hasChanges = delta && Object.keys(delta).length > 0;
                    
                    return (
                      <React.Fragment key={index}>
                        <TableRow 
                          sx={{ 
                            '&:hover': { backgroundColor: '#f8fafc' },
                            cursor: hasChanges ? 'pointer' : 'default'
                          }}
                          onClick={() => hasChanges && onRowToggle(index)}
                        >
                          <TableCell>
                            {hasChanges && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRowToggle(index);
                                }}
                              >
                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {new Date(item.changed_at + "Z").toLocaleString(undefined, {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                timeZoneName: 'short'
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.change_type}
                              size="small"
                              sx={{
                                backgroundColor:
                                  item.change_type === "CREATE"
                                    ? "#059669"
                                    : item.change_type === "UPDATE"
                                    ? "#3b82f6"
                                    : "#dc2626",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {item.changed_by}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {hasChanges ? (
                              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {Object.keys(delta).length} field(s) changed
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                No changes
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        
                        {hasChanges && (
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 1, p: 2, backgroundColor: "#f8fafc", borderRadius: "6px" }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "#374151" }}>
                                    Changes Made:
                                  </Typography>
                                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                            {Object.entries(delta).map(([key, value], deltaIndex) => {
                                          // Handle special cases for accounts_added and accounts_removed
                                          if (key === "accounts_added" || key === "accounts_removed") {
                                            const accounts = Array.isArray(value) ? value : [];
                                            if (accounts.length === 0) return null; // Don't show empty arrays
                                            
                                            return (
                                              <Box key={deltaIndex} sx={{ 
                                                display: "flex", 
                                                flexDirection: "column",
                                                p: 1.5,
                                                backgroundColor: "white",
                                                borderRadius: "4px",
                                                border: "1px solid #e5e7eb"
                                              }}>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 600,
                                                    color: "#374151",
                                                    fontSize: "0.875rem",
                                                    mb: 1,
                                                    textTransform: "capitalize"
                                                  }}
                                                >
                                                  {key === "accounts_added" ? "Accounts Added" : "Accounts Removed"}
                                                </Typography>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                  {accounts.map((account, accIndex) => (
                                                    <Typography
                                                      key={accIndex}
                                                      variant="body2"
                                                      sx={{
                                                        color: key === "accounts_added" ? "#059669" : "#dc2626",
                                                        fontSize: "0.875rem",
                                                        fontFamily: "monospace",
                                                        backgroundColor: key === "accounts_added" ? "#f0fdf4" : "#fef2f2",
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: "4px",
                                                        border: `1px solid ${key === "accounts_added" ? "#bbf7d0" : "#fecaca"}`,
                                                        textAlign: "center",
                                                        fontWeight: 600
                                                      }}
                                                    >
                                                      {account}
                                                    </Typography>
                                                  ))}
                                                </Box>
                                              </Box>
                                            );
                                          }
                                          
                                          // Handle regular delta fields
                                          return (
                                            <Box key={deltaIndex} sx={{ 
                                              display: "flex", 
                                              flexDirection: "column",
                                              p: 1.5,
                                              backgroundColor: "white",
                                              borderRadius: "4px",
                                              border: "1px solid #e5e7eb"
                                            }}>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: "#374151",
                                                  fontSize: "0.875rem",
                                                  mb: 1,
                                                  textTransform: "capitalize"
                                                }}
                                              >
                                                {key.replace(/_/g, " ")}
                                              </Typography>
                                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                  <Typography variant="caption" sx={{ color: "#64748b", mb: 0.5 }}>
                                                    Previous Value
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      color: "#dc2626",
                                                      fontSize: "0.875rem",
                                                      fontFamily: "monospace",
                                                      backgroundColor: "#fef2f2",
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: "4px",
                                                      border: "1px solid #fecaca",
                                                      minWidth: "80px",
                                                      textAlign: "center"
                                                    }}
                                                  >
                                                    {key === "amount" ? (value.from ? `$${parseFloat(value.from).toLocaleString()}` : "N/A") : (value.from || "N/A")}
                                                  </Typography>
                                                </Box>
                                                
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      color: "#64748b",
                                                      fontSize: "1rem",
                                                      fontWeight: 600
                                                    }}
                                                  >
                                                    →
                                                  </Typography>
                                                </Box>
                                                
                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                  <Typography variant="caption" sx={{ color: "#64748b", mb: 0.5 }}>
                                                    New Value
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      color: "#059669",
                                                      fontSize: "0.875rem",
                                                      fontFamily: "monospace",
                                                      backgroundColor: "#f0fdf4",
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: "4px",
                                                      border: "1px solid #bbf7d0",
                                                      minWidth: "80px",
                                                      textAlign: "center"
                                                    }}
                                                  >
                                                    {key === "amount" ? (value.to ? `$${parseFloat(value.to).toLocaleString()}` : "N/A") : (value.to || "N/A")}
                                                    {key === "amount" && (() => {
                                                      const percentageChange = calculatePercentageChange(value.from, value.to);
                                                      if (percentageChange !== null) {
                                                        const sign = percentageChange >= 0 ? "+" : "";
                                                        const color = percentageChange >= 0 ? "#059669" : "#dc2626";
                                                        return (
                                                          <Box
                                                            component="span"
                                                            sx={{
                                                              display: "block",
                                                              fontSize: "0.75rem",
                                                              color: color,
                                                              fontWeight: "600",
                                                              mt: 0.5
                                                            }}
                                                          >
                                                            ({sign}{percentageChange.toFixed(1)}%)
                                                          </Box>
                                                        );
                                                      }
                                                      return null;
                                                    })()}
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            </Box>
                                          );
                                        })}
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#64748b", textAlign: "center" }}
              >
                No budget history data available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

// Helper function to calculate percentage change for amount fields
const calculatePercentageChange = (fromValue, toValue) => {
  if (!fromValue || !toValue || fromValue === "N/A" || toValue === "N/A") {
    return null;
  }
  
  const from = parseFloat(fromValue);
  const to = parseFloat(toValue);
  
  if (isNaN(from) || isNaN(to) || from === 0) {
    return null;
  }
  
  const percentageChange = ((to - from) / from) * 100;
  return percentageChange;
};

const SpendCategorization = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [availableAccountsModalOpen, setAvailableAccountsModalOpen] =
    useState(false);
  const [
    defaultResourceGroupAccountsModalOpen,
    setDefaultResourceGroupAccountsModalOpen,
  ] = useState(false);
  const [assignedAccountsModalOpen, setAssignedAccountsModalOpen] = useState(false);

  // Data fetch timestamp state
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Organization Budget state
  const [orgBudgetModalOpen, setOrgBudgetModalOpen] = useState(false);
  const [editingOrgBudget, setEditingOrgBudget] = useState(null);
  const [deleteOrgBudgetDialogOpen, setDeleteOrgBudgetDialogOpen] =
    useState(false);
  const [orgBudgetToDelete, setOrgBudgetToDelete] = useState(null);

  // Budget History Modal state
  const [budgetHistoryModalOpen, setBudgetHistoryModalOpen] = useState(false);
  const [selectedBudgetForHistory, setSelectedBudgetForHistory] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Organization Budget History Modal state
  const [orgBudgetHistoryModalOpen, setOrgBudgetHistoryModalOpen] = useState(false);
  const [expandedOrgBudgetRows, setExpandedOrgBudgetRows] = useState(new Set());

  // Organization Budget Alerts Modal state
  const [orgBudgetAlertsModalOpen, setOrgBudgetAlertsModalOpen] = useState(false);

  // Budget Group Alerts Modal state
  const [budgetGroupAlertsModalOpen, setBudgetGroupAlertsModalOpen] = useState(false);
  const [selectedBudgetGroup, setSelectedBudgetGroup] = useState(null);

  // MUI Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    budgetRecords,
    isFetchingBudgetRecords,
    fetchBudgetRecordsError,
    resourceConfigs,
    isFetchingResourceConfigs,
    fetchResourceConfigsError,
    createBudgetRecordSuccess,
    createBudgetRecordError,
    updateBudgetRecordSuccess,
    updateBudgetRecordError,
    deleteBudgetRecordSuccess,
    deleteBudgetRecordError,
    isDeletingBudgetRecord,
    // Organization Budget state
    orgBudgets,
    isFetchingOrgBudgets,
    fetchOrgBudgetsError,
    isCreatingOrgBudget,
    createOrgBudgetSuccess,
    createOrgBudgetError,
    isUpdatingOrgBudget,
    updateOrgBudgetSuccess,
    updateOrgBudgetError,
    isDeletingOrgBudget,
    deleteOrgBudgetSuccess,
    deleteOrgBudgetError,
    // Budget History state
    budgetHistory,
    isFetchingBudgetHistory,
    fetchBudgetHistoryError,
    // Organization Budget History state
    orgBudgetHistory,
    isFetchingOrgBudgetHistory,
    fetchOrgBudgetHistoryError,
  } = useSelector((state) => state.budget);

  // Get user profile for admin check
  const profile = useSelector((state) => state.profile);
  const isAdmin = profile?.userRoles?.some((role) => role === "Administrator") || false;

  useEffect(() => {
    // Fetch budget records and resource configs on component mount
    console.log(
      "🔄 Component: Fetching budget records, resource configs, and org budgets"
    );
    dispatch(getBudgetRecords());
    dispatch(getResourceConfigs());
    dispatch(getOrgBudgets());
    // Set the fetch time when making the request
    setLastFetchTime(new Date());

    return () => {
      dispatch(clearBudgetState());
    };
  }, [dispatch, history]);

  // Handle success/error messages
  useEffect(() => {
    if (createBudgetRecordSuccess) {
      const message = createBudgetRecordSuccess.message || "Budget record created successfully!";
      setSnackbar({
        open: true,
        message: message,
        severity: "success",
      });
      // Close modal and clear form state
      setIsModalOpen(false);
      setEditingRecord(null);
      // Refresh both budget records and organization budgets to update UI
      dispatch(getBudgetRecords());
      dispatch(getOrgBudgets());
      setLastFetchTime(new Date());
      // Clear success message
      dispatch(clearBudgetMessages());
    }
    if (createBudgetRecordError) {
      setSnackbar({
        open: true,
        message: createBudgetRecordError,
        severity: "error",
      });
      // Clear error message
      dispatch(clearBudgetMessages());
    }
    if (updateBudgetRecordSuccess) {
      const message = updateBudgetRecordSuccess.message || "Budget record updated successfully!";
      setSnackbar({
        open: true,
        message: message,
        severity: "success",
      });
      // Close modal and clear editing state
      setIsModalOpen(false);
      setEditingRecord(null);
      // Refresh both budget records and organization budgets to update UI
      dispatch(getBudgetRecords());
      dispatch(getOrgBudgets());
      setLastFetchTime(new Date());
      // Clear success message
      dispatch(clearBudgetMessages());
    }
    if (updateBudgetRecordError) {
      setSnackbar({
        open: true,
        message: updateBudgetRecordError,
        severity: "error",
      });
      // Clear error message
      dispatch(clearBudgetMessages());
    }
    if (deleteBudgetRecordSuccess) {
      const message = deleteBudgetRecordSuccess.message || "Budget record deleted successfully!";
      setSnackbar({
        open: true,
        message: message,
        severity: "success",
      });
      // Close delete dialog and clear delete state
      setDeleteDialogOpen(false);
      setRecordToDelete(null);
      // Refresh both budget records and organization budgets to update UI
      dispatch(getBudgetRecords());
      dispatch(getOrgBudgets());
      setLastFetchTime(new Date());
      // Clear success message
      dispatch(clearBudgetMessages());
    }
    if (deleteBudgetRecordError) {
      setSnackbar({
        open: true,
        message: deleteBudgetRecordError,
        severity: "error",
      });
      // Clear error message
      dispatch(clearBudgetMessages());
    }
    if (fetchResourceConfigsError) {
      setSnackbar({
        open: true,
        message: fetchResourceConfigsError,
        severity: "error",
      });
      // Clear error message
      dispatch(clearBudgetMessages());
    }

    // Organization Budget success/error handlers
    if (createOrgBudgetSuccess) {
      const message =
        createOrgBudgetSuccess.message ||
        "Organization budget created successfully!";
      setSnackbar({
        open: true,
        message: message,
        severity: "success",
      });
      setOrgBudgetModalOpen(false);
      setEditingOrgBudget(null);
      // Refresh both organization budgets and budget records to update UI
      dispatch(getOrgBudgets());
      dispatch(getBudgetRecords());
      setLastFetchTime(new Date());
      dispatch(clearBudgetMessages());
    }
    if (createOrgBudgetError) {
      setSnackbar({
        open: true,
        message: createOrgBudgetError,
        severity: "error",
      });
      dispatch(clearBudgetMessages());
    }
    if (updateOrgBudgetSuccess) {
      const message =
        updateOrgBudgetSuccess.message ||
        "Organization budget updated successfully!";
      setSnackbar({
        open: true,
        message: message,
        severity: "success",
      });
      setOrgBudgetModalOpen(false);
      setEditingOrgBudget(null);
      // Refresh both organization budgets and budget records to update UI
      dispatch(getOrgBudgets());
      dispatch(getBudgetRecords());
      setLastFetchTime(new Date());
      dispatch(clearBudgetMessages());
    }
    if (updateOrgBudgetError) {
      setSnackbar({
        open: true,
        message: updateOrgBudgetError,
        severity: "error",
      });
      dispatch(clearBudgetMessages());
    }
    if (deleteOrgBudgetSuccess) {
      const message =
        deleteOrgBudgetSuccess.message ||
        "Organization budget deleted successfully!";
      setSnackbar({
        open: true,
        message: message,
        severity: "success",
      });
      setDeleteOrgBudgetDialogOpen(false);
      setOrgBudgetToDelete(null);
      // Refresh both organization budgets and budget records to update UI
      dispatch(getOrgBudgets());
      dispatch(getBudgetRecords());
      setLastFetchTime(new Date());
      dispatch(clearBudgetMessages());
    }
    if (deleteOrgBudgetError) {
      setSnackbar({
        open: true,
        message: deleteOrgBudgetError,
        severity: "error",
      });
      dispatch(clearBudgetMessages());
    }

    if (createBudgetRecordError) {
      setSnackbar({
        open: true,
        message: createBudgetRecordError,
        severity: "error",
      });
      dispatch(clearBudgetMessages());
    }

    if (updateBudgetRecordError) {
      setSnackbar({
        open: true,
        message: updateBudgetRecordError,
        severity: "error",
      });
      dispatch(clearBudgetMessages());
    }

    if (deleteBudgetRecordError) {
      setSnackbar({
        open: true,
        message: deleteBudgetRecordError,
        severity: "error",
      });
      dispatch(clearBudgetMessages());
    }
  }, [
    createBudgetRecordSuccess,
    createBudgetRecordError,
    updateBudgetRecordSuccess,
    updateBudgetRecordError,
    deleteBudgetRecordSuccess,
    deleteBudgetRecordError,
    fetchResourceConfigsError,
    createOrgBudgetSuccess,
    createOrgBudgetError,
    updateOrgBudgetSuccess,
    updateOrgBudgetError,
    deleteOrgBudgetSuccess,
    deleteOrgBudgetError,
    dispatch,
  ]);

  const handleAddRecord = () => {
    setEditingRecord(null);
    setIsModalOpen(true);
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setIsModalOpen(true);
  };

  const handleDeleteRecord = (record) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    // Clear any pending messages to prevent issues
    dispatch(clearBudgetMessages());
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  const handleConfirmDeleteRecord = () => {
    if (recordToDelete) {
      dispatch(deleteBudgetRecord(recordToDelete.budget_id));
    }
  };

  const handleViewAccounts = (record) => {
    setSelectedRecord(record);
    setAccountsModalOpen(true);
  };

  const handleCloseAccountsModal = () => {
    setAccountsModalOpen(false);
    setSelectedRecord(null);
  };

  const handleOpenAvailableAccountsModal = () => {
    setAvailableAccountsModalOpen(true);
  };

  const handleCloseAvailableAccountsModal = () => {
    setAvailableAccountsModalOpen(false);
  };

  const handleOpenDefaultResourceGroupAccountsModal = () => {
    setDefaultResourceGroupAccountsModalOpen(true);
  };

  const handleCloseDefaultResourceGroupAccountsModal = () => {
    setDefaultResourceGroupAccountsModalOpen(false);
  };

  const handleOpenAssignedAccountsModal = () => {
    setAssignedAccountsModalOpen(true);
  };

  const handleCloseAssignedAccountsModal = () => {
    setAssignedAccountsModalOpen(false);
  };

  // Organization Budget handlers
  const handleAddOrgBudget = () => {
    setEditingOrgBudget(null);
    setOrgBudgetModalOpen(true);
  };

  const handleEditOrgBudget = (budget) => {
    setEditingOrgBudget(budget);
    setOrgBudgetModalOpen(true);
  };

  const handleDeleteOrgBudget = (budget) => {
    setOrgBudgetToDelete(budget);
    setDeleteOrgBudgetDialogOpen(true);
  };

  const handleCloseOrgBudgetModal = () => {
    setOrgBudgetModalOpen(false);
    setEditingOrgBudget(null);
    dispatch(clearBudgetMessages());
  };

  const handleCloseDeleteOrgBudgetDialog = () => {
    setDeleteOrgBudgetDialogOpen(false);
    setOrgBudgetToDelete(null);
  };

  const handleConfirmDeleteOrgBudget = () => {
    if (orgBudgetToDelete) {
      dispatch(deleteOrgBudget(orgBudgetToDelete.budget_id));
    }
  };

  const handleSubmitOrgBudget = (formData) => {
    if (editingOrgBudget) {
      // For PUT request, send amount, description, start_date, end_date, and contact fields in body, budget_id in query params
      const { amount, description, start_date, end_date, admin_name, admin_email, admin_phone } = formData;
      dispatch(
        updateOrgBudget({
          amount,
          description,
          start_date,
          end_date,
          admin_name,
          admin_email,
          admin_phone,
          budgetId: editingOrgBudget.budget_id,
        })
      );
    } else {
      dispatch(createOrgBudget(formData));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Budget History Modal handlers
  const handleOpenBudgetHistory = (record) => {
    setSelectedBudgetForHistory(record);
    setBudgetHistoryModalOpen(true);
    setExpandedRows(new Set());
    // Fetch budget history for this specific budget
    dispatch(getBudgetHistory(record.budget_id));
  };

  const handleCloseBudgetHistoryModal = () => {
    setBudgetHistoryModalOpen(false);
    setSelectedBudgetForHistory(null);
    setExpandedRows(new Set());
  };

  const handleRowToggle = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  // Organization Budget History Modal handlers
  const handleOpenOrgBudgetHistory = () => {
    setOrgBudgetHistoryModalOpen(true);
    setExpandedOrgBudgetRows(new Set());
    
    // Get active organization budget ID and fetch history
    const activeBudgets = orgBudgets
      ? orgBudgets.filter((budget) => budget.status !== "ARCHIVED")
      : [];
    
    if (activeBudgets.length > 0) {
      // Directly fetch organization budget history for the active budget
      dispatch(getOrgBudgetHistory(activeBudgets[0].budget_id));
    }
  };

  const handleCloseOrgBudgetHistoryModal = () => {
    setOrgBudgetHistoryModalOpen(false);
    setExpandedOrgBudgetRows(new Set());
  };

  // Organization Budget Alerts Modal handlers
  const handleOpenOrgBudgetAlerts = () => {
    setOrgBudgetAlertsModalOpen(true);
  };

  const handleCloseOrgBudgetAlertsModal = () => {
    setOrgBudgetAlertsModalOpen(false);
  };

  const handleOpenBudgetGroupAlerts = (budgetGroup) => {
    setSelectedBudgetGroup(budgetGroup);
    setBudgetGroupAlertsModalOpen(true);
  };

  const handleCloseBudgetGroupAlertsModal = () => {
    setBudgetGroupAlertsModalOpen(false);
    setSelectedBudgetGroup(null);
  };


  const handleOrgBudgetRowToggle = (budgetId) => {
    const newExpandedRows = new Set(expandedOrgBudgetRows);
    if (newExpandedRows.has(budgetId)) {
      newExpandedRows.delete(budgetId);
    } else {
      newExpandedRows.add(budgetId);
    }
    setExpandedOrgBudgetRows(newExpandedRows);
  };

  // Format the last fetch time for display
  const formatLastFetchTime = () => {
    if (!lastFetchTime) return null;
    
    return lastFetchTime.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Utility functions for Organization Budget
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const getStatusColor = (startDate, endDate, status) => {
    // If status is ARCHIVED, return gray
    if (status === "ARCHIVED") return "#6b7280";

    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) return "#3b82f6"; // Future - Blue
    if (now >= start && now <= end) return "#10b981"; // Active - Green
    return "#6b7280"; // Expired - Gray
  };

  const getStatusText = (startDate, endDate, status) => {
    // If status is ARCHIVED, return Archived
    if (status === "ARCHIVED") return "Archived";

    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) return "Future";
    if (now >= start && now <= end) return "Active";
    return "Expired";
  };

  // Calculate dynamic width for Group column based on content
  const calculateGroupColumnWidth = (data) => {
    if (!data || data.length === 0) return 200; // Default width
    
    const maxLength = Math.max(
      ...data.map(row => row.resource_group_name ? row.resource_group_name.length : 0)
    );
    
    // Calculate width based on character count (approximately 8px per character)
    // Minimum 120px, maximum 240px (30 characters * 8px)
    const calculatedWidth = Math.max(120, Math.min(240, maxLength * 8 + 40));
    
    return calculatedWidth;
  };

  // Define columns for the DataGrid based on API response
  const columns = [
    {
      field: "resource_group_name",
      headerName: "Group",
      flex: 0,
      width: calculateGroupColumnWidth(budgetRecords),
      resizable: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            padding: "4px 0",
            transition: "all 0.2s ease-in-out",
          }}
                     onClick={() => {
             const encodedDepartmentName = encodeURIComponent(params.value);
             const encodedStartDate = encodeURIComponent(params.row.start_date);
             const encodedEndDate = encodeURIComponent(params.row.end_date);
             // Pass account numbers as comma-separated string
             const accountNumbers = params.row.accounts?.map(acc => acc.accnumber).join(',') || '';
             const encodedAccountNumbers = encodeURIComponent(accountNumbers);
            history.push(
              `/group-based-report/${encodedDepartmentName}/${encodedStartDate}/${encodedEndDate}/${encodedAccountNumbers}`
            );
           }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "0.875rem",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              textDecoration: "underline",
              textDecorationColor: "#0073bb",
              textUnderlineOffset: "3px",
              textDecorationThickness: "1px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                textDecorationColor: "#005a9d",
              },
            }}
          >
            {params.value}
          </Typography>
          <Box
            sx={{
              ml: 1,
              display: "flex",
              alignItems: "center",
              color: "#3b82f6",
              fontSize: "12px",
              opacity: 0.7,
              transition: "opacity 0.2s ease-in-out",
              "&:hover": {
                opacity: 1,
              },
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        </Box>
      ),
    },
    {
      field: "budget",
      headerName: "Allocated Budget",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#374151",
              fontSize: "0.875rem",
              fontWeight: 400,
            }}
          >
            {params.row.amount
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(params.row.amount)
              : "-"}
            {params.row.percent_of_org_budget && (
              <Typography
                component="span"
                sx={{
                  color: "#6b7280",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  ml: 0.5,
                }}
              >
                ({params.row.percent_of_org_budget}% of org budget)
              </Typography>
            )}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#6b7280",
              fontSize: "0.75rem",
              fontWeight: 400,
            }}
          >
            {params.row.start_date} → {params.row.end_date}
          </Typography>
        </Box>
      ),
    },
    {
      field: "spent",
      headerName: "Actual Spend",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const actualSpend = params.row.total_usage || 0;
        const spendPct = params.row.actual_spend_pct || 0;

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#374151",
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {actualSpend !== undefined && actualSpend !== null
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(actualSpend)
                : "-"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                fontSize: "0.75rem",
                fontWeight: 400,
              }}
            >
              {spendPct !== undefined && spendPct !== null
                ? `${spendPct.toFixed(1)}% of the budget`
                : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "variance",
      headerName: "Budget Status",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => {
        const actualDiff = params.row.actual_difference;
        const actualDiffPct = params.row.actual_difference_pct;

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: actualDiff < 0 ? "#dc2626" : "#374151",
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {actualDiff !== undefined && actualDiff !== null
                ? actualDiff >= 0
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(actualDiff)
                  : `(${new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(Math.abs(actualDiff))})`
                : "-"}
            </Typography>
            {actualDiffPct !== undefined && actualDiffPct !== null && (
              <Typography
                variant="caption"
                sx={{
                  color: actualDiff < 0 ? "#dc2626" : "#64748b",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                }}
              >
                {actualDiff >= 0 
                  ? `${actualDiffPct?.toFixed(1) || '0.0'}% of the budget remaining`
                  : `${Math.abs(actualDiffPct)?.toFixed(1) || '0.0'}% over budget`
                }
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "forecast",
      headerName: "Forecast",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const forecastedSpend = params.row.usage_forecast;
        const forecastedPct = params.row.forecasted_spend_pct;
        
        // Determine color based on forecast percentage
        let forecastColor = "#059669"; // Green (under 90%)
        if (forecastedPct >= 110) {
          forecastColor = "#dc2626"; // Red (over 110%)
        } else if (forecastedPct >= 90) {
          forecastColor = "#d97706"; // Amber (90-110%)
        }

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: forecastColor,
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {forecastedSpend !== undefined && forecastedSpend !== null
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(forecastedSpend)
                : "-"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: forecastColor,
                fontSize: "0.75rem",
                fontWeight: 400,
              }}
            >
              {forecastedPct !== undefined && forecastedPct !== null
                ? `${forecastedPct.toFixed(1)}% of the budget`
                : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "forecastedVariance",
      headerName: "Forecasted Variance",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const forecastedVariance = params.row.forecasted_variance;
        const forecastedVariancePct = params.row.forecasted_variance_pct;
        
        // Use the same color logic as the Forecast column
        const forecastedPct = params.row.forecasted_spend_pct || 0;
        let varianceColor = "#059669"; // Green (under 90%)
        if (forecastedPct >= 110) {
          varianceColor = "#dc2626"; // Red (over 110%)
        } else if (forecastedPct >= 90) {
          varianceColor = "#d97706"; // Amber (90-110%)
        }

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: varianceColor,
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {forecastedVariance !== undefined && forecastedVariance !== null
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    signDisplay: "never"
                  }).format(Math.abs(forecastedVariance))
                : "-"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: varianceColor,
                fontSize: "0.75rem",
                fontWeight: 400,
              }}
            >
              {forecastedVariancePct !== undefined && forecastedVariancePct !== null
                ? forecastedVariancePct < 0
                  ? `${Math.abs(forecastedVariancePct).toFixed(1)}% over budget`
                  : `${forecastedVariancePct.toFixed(1)}% under budget`
                : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "admin_name",
      headerName: "Contact",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#3b82f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          >
            {params.value ? params.value.charAt(0).toUpperCase() : "U"}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "0.8rem",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                {params.value}
              </Typography>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Contact Details
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ color: "white" }}>
                        <strong>Name:</strong> {params.value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "white" }}>
                        <strong>Email:</strong>{" "}
                        {params.row.admin_email || "Not provided"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "white" }}>
                        <strong>Phone:</strong>{" "}
                        {params.row.admin_phone || "Not provided"}
                      </Typography>
                    </Box>
                  </Box>
                }
                arrow
                placement="top"
              >
                <InfoIcon
                  sx={{
                    fontSize: 14,
                    color: "#64748b",
                    cursor: "help",
                    "&:hover": {
                      color: "#3b82f6",
                    },
                  }}
                />
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontSize: "0.7rem",
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              {params.row.admin_email || "No email"}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "accounts",
      headerName: "Accounts",
      flex: 0,
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const accountCount = params.value ? params.value.length : 0;
        const isDisabled = accountCount === 0;
        
        return (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isDisabled ? "not-allowed" : "pointer",
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: isDisabled ? "#f3f4f6" : "#f0f9ff",
              border: isDisabled ? "1px solid #e2e8f0" : "1px solid #3b82f6",
              transition: "all 0.2s ease-in-out",
              opacity: isDisabled ? 0.6 : 1,
              ...(isDisabled ? {} : {
                "&:hover": {
                  backgroundColor: "#dbeafe",
                  borderColor: "#2563eb",
                  boxShadow: "0 1px 3px rgba(59, 130, 246, 0.2)",
                },
              }),
            }}
            onClick={(e) => {
              if (!isDisabled) {
                e.stopPropagation();
                handleViewAccounts(params.row);
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isDisabled ? "#9ca3af" : "#3b82f6",
                fontSize: "0.75rem",
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                lineHeight: 1,
              }}
            >
              {accountCount}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Show History" arrow placement="top">
            <IconButton
              size="small"
              onClick={() => handleOpenBudgetHistory(params.row)}
              sx={{
                color: "#64748b",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                  color: "#3b82f6",
                },
              }}
            >
              <HistoryIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Budget Group Alerts" arrow placement="top">
            <IconButton
              size="small"
              onClick={() => handleOpenBudgetGroupAlerts(params.row)}
              sx={{
                color: "#64748b",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                  color: "#f59e0b",
                },
              }}
            >
              <NotificationsIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <>
              <Tooltip title="Edit Budget Group" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => handleEditRecord(params.row)}
                  sx={{
                    color: "#64748b",
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                      color: "#10b981",
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Budget Group" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteRecord(params.row)}
                  sx={{
                    color: "#64748b",
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "#fef2f2",
                      color: "#ef4444",
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {!isAdmin && (
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontSize: "0.7rem",
                fontStyle: "italic",
              }}
            >
              Admin Only
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  // Transform budget records for DataGrid
  const rows = budgetRecords.map((record, index) => ({
    id: record.budget_id || index,
    ...record,
  }));

  // Debug logging
  console.log("🔍 Component: budgetRecords =", budgetRecords);
  console.log("🔍 Component: rows =", rows);

  return (
    <Box
      sx={{
        padding: "1.5rem 2rem",
        mt: "4rem",
        mb: "3rem",
        backgroundColor: "#F9F9F9",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Modern Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            p: 3,
            backgroundColor: "white",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#e0f2fe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #0288d1",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L2 7L12 12L22 7L12 2Z"
                  stroke="#0288d1"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 17L12 22L22 17"
                  stroke="#0288d1"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 12L12 17L22 12"
                  stroke="#0288d1"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#01579b",
                  fontSize: "1.75rem",
                  letterSpacing: "-0.025em",
                  mb: 0.5,
                }}
              >
                Spend Categorization
              </Typography>
              {formatLastFetchTime() && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    fontStyle: "italic",
                  }}
                >
                  Data fetched on {formatLastFetchTime()}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Account Buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleOpenAvailableAccountsModal}
              sx={{
                borderColor: "#f59e0b",
                color: "#f59e0b",
                backgroundColor: "#fffbeb",
                fontWeight: 600,
                fontSize: "0.875rem",
                padding: "8px 16px",
                borderRadius: "8px",
                textTransform: "none",
                minWidth: "auto",
                "&:hover": {
                  borderColor: "#d97706",
                  backgroundColor: "#fef3c7",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 4px rgba(245, 158, 11, 0.2)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              UnAssigned Accounts:{" "}
              {isFetchingResourceConfigs
                ? "Loading..."
                : resourceConfigs?.accounts
                ? (() => {
                    const accountCount = resourceConfigs.accounts.length;
                    const totalSpend = resourceConfigs.accounts.reduce((sum, account) => sum + (account.org_budget_usage || 0), 0);
                    return `${accountCount}, Spend: ${new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(totalSpend)}`;
                  })()
                : "0, Spend: $0.00"}
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleOpenAssignedAccountsModal}
              sx={{
                borderColor: "#059669",
                color: "#059669",
                backgroundColor: "#f0fdf4",
                fontWeight: 600,
                fontSize: "0.875rem",
                padding: "8px 16px",
                borderRadius: "8px",
                textTransform: "none",
                minWidth: "auto",
                "&:hover": {
                  borderColor: "#047857",
                  backgroundColor: "#ecfdf5",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 4px rgba(5, 150, 105, 0.2)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              Assigned Accounts:{" "}
              {isFetchingResourceConfigs
                ? "Loading..."
                : resourceConfigs?.accounts_with_budgets
                ? resourceConfigs.accounts_with_budgets.length
                : 0}
            </Button>
          </Box>
        </Box>

        {/* Organization Budget Section */}
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Section Header */}
            <Box
              sx={{
                p: 2.5,
                borderBottom: "1px solid #f1f5f9",
                backgroundColor: "#fafbfc",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: "#3b82f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccountBalanceIcon sx={{ fontSize: 18, color: "white" }} />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "1rem",
                      }}
                    >
                      Organization Budget
                    </Typography>
                    {(() => {
                      const activeBudgets = orgBudgets
                        ? orgBudgets.filter((budget) => budget.status !== "ARCHIVED")
                        : [];
                      
                      if (activeBudgets.length > 0) {
                        return (
                          <Chip
                            label={getStatusText(
                              activeBudgets[0].start_date,
                              activeBudgets[0].end_date,
                              activeBudgets[0].status
                            )}
                            sx={{
                              backgroundColor:
                                getStatusColor(
                                  activeBudgets[0].start_date,
                                  activeBudgets[0].end_date,
                                  activeBudgets[0].status
                                ) + "20",
                              color: getStatusColor(
                                activeBudgets[0].start_date,
                                activeBudgets[0].end_date,
                                activeBudgets[0].status
                              ),
                              fontWeight: 600,
                              fontSize: "0.65rem",
                              height: "20px",
                            }}
                            size="small"
                          />
                        );
                      }
                      return null;
                    })()}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748b",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {(() => {
                      const activeBudgets = orgBudgets
                        ? orgBudgets.filter((budget) => budget.status !== "ARCHIVED")
                        : [];
                      
                      if (activeBudgets.length > 0 && activeBudgets[0].description) {
                        return activeBudgets[0].description;
                      }
                      return "Financial overview and budget tracking";
                    })()}
                  </Typography>
                </Box>
              </Box>

              {/* Header Actions */}
              {(() => {
                const activeBudgets = orgBudgets
                  ? orgBudgets.filter((budget) => budget.status !== "ARCHIVED")
                  : [];
                
                return (
                  <>
                    {!isFetchingOrgBudgets && activeBudgets.length > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={handleOpenOrgBudgetHistory}
                          sx={{
                            color: "#64748b",
                            padding: "6px",
                            borderRadius: "6px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                              color: "#3b82f6",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          <HistoryIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleOpenOrgBudgetAlerts}
                          sx={{
                            color: "#64748b",
                            padding: "6px",
                            borderRadius: "6px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                              color: "#f59e0b",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          <NotificationsIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditOrgBudget(activeBudgets[0])}
                          sx={{
                            color: "#64748b",
                            padding: "6px",
                            borderRadius: "6px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                              color: "#3b82f6",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteOrgBudget(activeBudgets[0])}
                          sx={{
                            color: "#64748b",
                            padding: "6px",
                            borderRadius: "6px",
                            "&:hover": {
                              backgroundColor: "#fef2f2",
                              color: "#dc2626",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              {fetchOrgBudgetsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {fetchOrgBudgetsError}
                </Alert>
              )}

              {/* Loading State */}
              {isFetchingOrgBudgets && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                  <CircularProgress size={20} sx={{ color: "#3b82f6" }} />
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Loading organization budget...
                  </Typography>
                </Box>
              )}

              {/* Filter out archived budgets and get active ones */}
              {(() => {
                const activeBudgets = orgBudgets
                  ? orgBudgets.filter((budget) => budget.status !== "ARCHIVED")
                  : [];

                return (
                  <>
                    {/* Empty State - No active budgets */}
                    {!isFetchingOrgBudgets && activeBudgets.length === 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "8px",
                              backgroundColor: "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <AccountBalanceIcon
                              sx={{ color: "#94a3b8", fontSize: 20 }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{ color: "#475569", fontWeight: 500 }}
                            >
                              No active organization budget
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#94a3b8", fontSize: "0.75rem" }}
                            >
                              Create a budget to start tracking your organization's spending
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleAddOrgBudget}
                          sx={{
                            backgroundColor: "#3b82f6",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            padding: "8px 16px",
                            borderRadius: "6px",
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: "#2563eb",
                            },
                          }}
                        >
                          Create Budget
                        </Button>
                      </Box>
                    )}

                    {/* Active Budget Display */}
                    {!isFetchingOrgBudgets && activeBudgets.length > 0 && (
                      <Box>
                        {/* Budget Overview Cards */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: 2,
                            mb: 3,
                          }}
                        >
                          {/* Total Budget Card */}
                          <Box
                            sx={{
                              p: 2.5,
                              backgroundColor: "#f8fafc",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                letterSpacing: "0.5px",
                                mb: 1,
                                display: "block",
                              }}
                            >
                              Total Budget
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: "#1e293b",
                                fontSize: "1.5rem",
                                mb: 0.5,
                              }}
                            >
                              {formatCurrency(
                                activeBudgets[0].amount,
                                activeBudgets[0].currency
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontSize: "0.75rem",
                              }}
                            >
                              {activeBudgets[0].start_date} - {activeBudgets[0].end_date}
                            </Typography>
                          </Box>

                          {/* Used Amount Card */}
                          <Box
                            sx={{
                              p: 2.5,
                              backgroundColor: "#fef7ed",
                              borderRadius: "8px",
                              border: "1px solid #fed7aa",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#92400e",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                letterSpacing: "0.5px",
                                mb: 1,
                                display: "block",
                              }}
                            >
                              Allocated Funds
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: "#c2410c",
                                fontSize: "1.5rem",
                                mb: 0.5,
                              }}
                            >
                              {formatCurrency(
                                activeBudgets[0].used_amount || 0,
                                activeBudgets[0].currency
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#92400e",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                              }}
                            >
                              {activeBudgets[0].used_pct?.toFixed(1) || 0}% of total budget
                            </Typography>
                          </Box>

                          {/* Remaining Amount Card */}
                          <Box
                            sx={{
                              p: 2.5,
                              backgroundColor: "#f0fdf4",
                              borderRadius: "8px",
                              border: "1px solid #bbf7d0",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#166534",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                letterSpacing: "0.5px",
                                mb: 1,
                                display: "block",
                              }}
                            >
                              Remaining Funds
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: "#15803d",
                                fontSize: "1.5rem",
                                mb: 0.5,
                              }}
                            >
                              {formatCurrency(
                                activeBudgets[0].remaining_amount || 0,
                                activeBudgets[0].currency
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#166534",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                              }}
                            >
                              {activeBudgets[0].remaining_pct?.toFixed(1) || 0}% of total budget
                            </Typography>
                          </Box>

                          {/* Budget Summary Card */}
                          <Box
                            sx={{
                              p: 2.5,
                              backgroundColor: "#f8fafc",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                letterSpacing: "0.5px",
                                mb: 1,
                                display: "block",
                              }}
                            >
                              Budget Summary
                            </Typography>
                            
                            {(() => {
                              // Calculate summations from budgetRecords
                              const totalActualSpend = budgetRecords.reduce((sum, record) => sum + (record.total_usage || 0), 0);
                              const totalForecast = budgetRecords.reduce((sum, record) => sum + (record.usage_forecast || 0), 0);
                              const totalVariance = budgetRecords.reduce((sum, record) => sum + (record.forecasted_variance || 0), 0);
                              
                              // Get currency from first record or default to USD
                              const currency = budgetRecords.length > 0 ? (budgetRecords[0].currency || "USD") : "USD";
                              
                              return (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                  {/* Top Row - Actual Spend and Forecast */}
                                  <Box sx={{ display: "flex", gap: 1.5 }}>
                                    {/* Actual Spend */}
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#64748b",
                                          fontSize: "0.6rem",
                                          fontWeight: 500,
                                          display: "block",
                                          mb: 0.25,
                                        }}
                                      >
                                        Actual Spend
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: "#1e293b",
                                          fontSize: "0.8rem",
                                        }}
                                      >
                                        {formatCurrency(totalActualSpend, currency)}
                                      </Typography>
                                    </Box>

                                    {/* Forecast */}
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#64748b",
                                          fontSize: "0.6rem",
                                          fontWeight: 500,
                                          display: "block",
                                          mb: 0.25,
                                        }}
                                      >
                                        Forecast
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: "#1e293b",
                                          fontSize: "0.8rem",
                                        }}
                                      >
                                        {formatCurrency(totalForecast, currency)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Bottom Row - Forecasted Variance */}
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#64748b",
                                        fontSize: "0.6rem",
                                        fontWeight: 500,
                                        display: "block",
                                        mb: 0.25,
                                      }}
                                    >
                                      Forecasted Variance
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#1e293b",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      {formatCurrency(totalVariance, currency)}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })()}
                          </Box>

                          {/* Organization Contact Details Card */}
                          <Box
                            sx={{
                              p: 2.5,
                              backgroundColor: "#f8fafc",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                letterSpacing: "0.5px",
                                mb: 1,
                                display: "block",
                              }}
                            >
                              Organization Contact Details
                            </Typography>
                            {activeBudgets[0].admin_name ? (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#1e293b",
                                    fontSize: "0.875rem",
                                    mb: 0.5,
                                  }}
                                >
                                  {activeBudgets[0].admin_name}
                                </Typography>
                                {activeBudgets[0].admin_email && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      display: "block",
                                      mb: 0.25,
                                    }}
                                  >
                                    {activeBudgets[0].admin_email}
                                  </Typography>
                                )}
                                {activeBudgets[0].admin_phone && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                      display: "block",
                                    }}
                                  >
                                    {activeBudgets[0].admin_phone}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    backgroundColor: "#e2e8f0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                                      stroke="#64748b"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z"
                                      stroke="#64748b"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#64748b",
                                    fontSize: "0.75rem",
                                    fontStyle: "italic",
                                  }}
                                >
                                  No contact details available
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Progress Bar Section */}
                        <Box sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "#374151",
                                fontSize: "0.875rem",
                              }}
                            >
                              Budget Utilization
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#6b7280",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                              }}
                            >
                              {activeBudgets[0].used_pct?.toFixed(1) || 0}% utilized
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#f3f4f6",
                              borderRadius: "6px",
                              overflow: "hidden",
                              position: "relative",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${activeBudgets[0].used_pct || 0}%`,
                                height: "100%",
                                background: "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)",
                                borderRadius: "6px",
                                transition: "width 0.5s ease-in-out",
                                boxShadow: "0 1px 3px rgba(245, 158, 11, 0.3)",
                              }}
                            />
                          </Box>
                        </Box>


                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Budget Records Section */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: "#0288d1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  fontSize: "1.125rem",
                  mb: 0.25,
                }}
              >
                Budget Groups
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {budgetRecords.length} active budget
                {budgetRecords.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip
            title={
              !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0
                ? "You must create an organization budget before creating budget groups"
                : !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0
                ? "No unassigned accounts available to create budget groups"
                : "Create a new budget group for resource management"
            }
            arrow
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddRecord}
                disabled={!orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0}
                sx={{
                  backgroundColor: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "#9ca3af" : "#3b82f6",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "none" : "0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)",
                  "&:hover": {
                    backgroundColor: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "#9ca3af" : "#2563eb",
                    boxShadow: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "none" : "0 6px 8px -1px rgba(59, 130, 246, 0.3), 0 4px 6px -1px rgba(59, 130, 246, 0.2)",
                    transform: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "none" : "translateY(-1px)",
                  },
                  "&:active": {
                    backgroundColor: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "#9ca3af" : "#1d4ed8",
                    transform: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "none" : "translateY(0)",
                  },
                  "&:disabled": {
                    backgroundColor: "#9ca3af",
                    color: "#6b7280",
                    cursor: "not-allowed",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                Create Budget Group
              </Button>
            </span>
          </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2 }}>
          {fetchBudgetRecordsError && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: "#fef2f2",
                borderRadius: "6px",
                border: "1px solid #f87171",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#991b1b",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                }}
              >
                {fetchBudgetRecordsError}
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {!isFetchingBudgetRecords && budgetRecords.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                px: 3,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: "#f1f5f9",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <AddIcon sx={{ color: "#64748b", fontSize: 24 }} />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  mb: 2,
                  fontSize: "0.8rem",
                }}
              >
                Get started by creating your first budget record
              </Typography>
              <Tooltip
                title={
                  !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0
                    ? "You must create an organization budget before creating budget groups"
                    : !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0
                    ? "No unassigned accounts available to create budget groups"
                    : "Create a new budget group for resource management"
                }
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddRecord}
                    disabled={!orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0}
                    sx={{
                      backgroundColor: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "#9ca3af" : "#3b82f6",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: !orgBudgets || orgBudgets.filter(budget => budget.status !== "ARCHIVED").length === 0 || !resourceConfigs?.accounts || resourceConfigs.accounts.length === 0 ? "#9ca3af" : "#2563eb",
                      },
                      "&:disabled": {
                        backgroundColor: "#9ca3af",
                        color: "#6b7280",
                        cursor: "not-allowed",
                      },
                    }}
                  >
                    Create Budget Group
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}

          {/* Loading Spinner */}
          {isFetchingBudgetRecords && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
              <Typography
                variant="body2"
                sx={{
                  ml: 1.5,
                  color: "#64748b",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                }}
              >
                Loading budget groups...
              </Typography>
            </Box>
          )}

          {/* Data Grid */}
          {!isFetchingBudgetRecords && budgetRecords.length > 0 && (
            <Box
              sx={{
                height: 600,
                "& .MuiDataGrid-root": {
                  border: "none",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow:
                    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#e0f2fe",
                  borderBottom: "2px solid #0288d1",
                  "& .MuiDataGrid-columnHeader": {
                    borderRight: "1px solid #b3e5fc",
                    "&:last-child": {
                      borderRight: "none",
                    },
                    "&:hover": {
                      backgroundColor: "#b3e5fc",
                    },
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 700,
                    color: "#01579b",
                    fontSize: "0.875rem",
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    letterSpacing: "0.5px",
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e3f2fd",
                  fontSize: "0.875rem",
                  color: "#374151",
                  "&:focus": {
                    outline: "none",
                  },
                  padding: "12px 16px",
                  minHeight: "60px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "#f3f8ff",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.2s ease-in-out",
                  },
                  "&:nth-of-type(even)": {
                    backgroundColor: "#fafbfc",
                  },
                  "&:nth-of-type(odd)": {
                    backgroundColor: "#ffffff",
                  },
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  borderBottom: "1px solid #e3f2fd",
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: "transparent",
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: "#fafbfc",
                  borderTop: "1px solid #e2e8f0",
                  "& .MuiTablePagination-root": {
                    color: "#1e293b",
                    fontWeight: 600,
                  },
                  "& .MuiTablePagination-select": {
                    color: "#1e293b",
                  },
                  "& .MuiTablePagination-selectIcon": {
                    color: "#1e293b",
                  },
                  "& .MuiIconButton-root": {
                    color: "#1e293b",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                    },
                  },
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeader:focus": {
                  outline: "none",
                },
              }}
            >
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={isFetchingBudgetRecords}
                disableSelectionOnClick
                disableColumnMenu
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 700,
                  },
                  "& .MuiDataGrid-cell": {
                    py: 1,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#e0f2fe",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#fafbfc",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Add/Edit Modal */}
      <BudgetRecordModal
        open={isModalOpen}
        onClose={handleCloseModal}
        record={editingRecord}
        resourceConfigs={resourceConfigs}
        isFetchingResourceConfigs={isFetchingResourceConfigs}
        orgBudgets={orgBudgets}
      />

      {/* Delete Confirmation Dialog */}
      <Modal
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-budget-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            p: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
          >
            Delete Budget Record
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 3 }}>
            Are you sure you want to delete this budget record? This action
            cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteDialog}
              sx={{
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDeleteRecord}
              disabled={isDeletingBudgetRecord}
              sx={{
                backgroundColor: "#ef4444",
                "&:hover": {
                  backgroundColor: "#dc2626",
                },
                "&:disabled": {
                  backgroundColor: "#d1d5db",
                },
              }}
            >
              {isDeletingBudgetRecord ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Accounts Modal */}
      <AccountsModal
        open={accountsModalOpen}
        onClose={handleCloseAccountsModal}
        record={selectedRecord}
      />

      {/* Available Accounts Modal */}
      <AvailableAccountsModal
        open={availableAccountsModalOpen}
        onClose={handleCloseAvailableAccountsModal}
        allAccounts={resourceConfigs?.accounts || []}
        accountsInDefaultResourceGroup={
          resourceConfigs?.accounts_in_default_resource_group || []
        }
      />

      {/* Default Resource Group Accounts Modal */}
      <DefaultResourceGroupAccountsModal
        open={defaultResourceGroupAccountsModalOpen}
        onClose={handleCloseDefaultResourceGroupAccountsModal}
        accountsInDefaultResourceGroup={
          resourceConfigs?.accounts_in_default_resource_group || []
        }
        allAccounts={resourceConfigs?.accounts || []}
      />

      {/* Assigned Accounts Modal */}
      <AssignedAccountsModal
        open={assignedAccountsModalOpen}
        onClose={handleCloseAssignedAccountsModal}
        assignedAccounts={resourceConfigs?.accounts_with_budgets || []}
      />

      {/* Organization Budget Modal */}
      <OrgBudgetModal
        open={orgBudgetModalOpen}
        onClose={handleCloseOrgBudgetModal}
        budget={editingOrgBudget}
        onSubmit={handleSubmitOrgBudget}
        isSubmitting={isCreatingOrgBudget || isUpdatingOrgBudget}
      />

      {/* Delete Organization Budget Dialog */}
      <Modal
        open={deleteOrgBudgetDialogOpen}
        onClose={handleCloseDeleteOrgBudgetDialog}
        aria-labelledby="delete-org-budget-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            p: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
          >
            Delete Organization Budget
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 3 }}>
            Are you sure you want to delete this organization budget? This
            action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteOrgBudgetDialog}
              sx={{
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDeleteOrgBudget}
              disabled={isDeletingOrgBudget}
              sx={{
                backgroundColor: "#ef4444",
                color: "white",
                "&:hover": {
                  backgroundColor: "#dc2626",
                },
                "&:disabled": {
                  backgroundColor: "#fca5a5",
                },
              }}
            >
              {isDeletingOrgBudget ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Budget History Modal */}
      <BudgetHistoryModal
        open={budgetHistoryModalOpen}
        onClose={handleCloseBudgetHistoryModal}
        budgetHistory={budgetHistory}
        isFetchingBudgetHistory={isFetchingBudgetHistory}
        fetchBudgetHistoryError={fetchBudgetHistoryError}
        expandedRows={expandedRows}
        onRowToggle={handleRowToggle}
        selectedBudget={selectedBudgetForHistory}
      />

      {/* Organization Budget History Modal */}
      <OrgBudgetHistoryModal
        open={orgBudgetHistoryModalOpen}
        onClose={handleCloseOrgBudgetHistoryModal}
        orgBudgetHistory={orgBudgetHistory}
        isFetchingOrgBudgetHistory={isFetchingOrgBudgetHistory}
        fetchOrgBudgetHistoryError={fetchOrgBudgetHistoryError}
        expandedRows={expandedOrgBudgetRows}
        onRowToggle={handleOrgBudgetRowToggle}
      />

      {/* Organization Budget Alerts Modal */}
      <OrgBudgetAlertsModal
        open={orgBudgetAlertsModalOpen}
        onClose={handleCloseOrgBudgetAlertsModal}
        orgBudgetId={orgBudgets && orgBudgets.filter((budget) => budget.status !== "ARCHIVED").length > 0 ? orgBudgets.filter((budget) => budget.status !== "ARCHIVED")[0].budget_id : null}
        orgBudgetName={orgBudgets && orgBudgets.filter((budget) => budget.status !== "ARCHIVED").length > 0 ? (orgBudgets.filter((budget) => budget.status !== "ARCHIVED")[0].description || "Organization Budget") : "Organization Budget"}
      />

      {/* Budget Group Alerts Modal */}
      <BudgetGroupAlertsModal
        open={budgetGroupAlertsModalOpen}
        onClose={handleCloseBudgetGroupAlertsModal}
        rgBudgetId={selectedBudgetGroup?.budget_id || null}
        budgetGroupName={selectedBudgetGroup?.resource_group_name || "Budget Group"}
      />

      {/* MUI Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default SpendCategorization;
